import * as XLSXStyle from "xlsx-js-style";
import round2Decimals from "./round2Decimals";
import numeroALetra from "./numeroALetra";
import PhoneMask from "./phoneMask";
import { inyectarLogoEnExcel } from "./ExcelLogo";
import {
  headerStyle, moneyStyle, textStyle, totalTextoStyle, totalMoneyStyle,
  celdaMoneda, celdaTexto, celdaNumero, celdaFecha,
  aNumero, nodoImpuestosLocales, nombreVendedor,
} from "./ExcelEstilos";

/**
 * Excel de UN documento: la representación en Excel de una cotización, pedido,
 * embarque, factura, remisión u orden de compra. Encabezado con el logo y los
 * datos de la empresa, el documento y el cliente (el proveedor, en compras);
 * abajo la tabla de partidas y el bloque de totales.
 *
 * Se arma con el mismo `values` que consume el generador de PDF
 * (`<modulo>/datosPdf` o `datosPdfMasivo`), así que los números salen de la
 * misma fuente y no cuesta ninguna consulta extra.
 *
 * A diferencia del PDF, las columnas de la tabla no siguen las banderas de la
 * empresa que ocultan Clave SAT / Clave / P.U. / IVA / Importe: son las mismas
 * siempre, salvo por el juego de columnas propio de compras.
 */

// El encabezado no lleva la retícula de la tabla, así que estos estilos van sin
// `border`.
const tituloEmpresaStyle = { font: { bold: true, sz: 14 }, alignment: { vertical: "center" } };
const etiquetaStyle = { font: { bold: true, sz: 10 }, alignment: { vertical: "center" } };
const valorStyle = { font: { sz: 10 }, alignment: { vertical: "center", wrapText: true } };
const valorFechaStyle = { font: { sz: 10 }, alignment: { horizontal: "left", vertical: "center" } };
const tituloDocumentoStyle = { font: { bold: true, sz: 12 }, alignment: { vertical: "center" } };
const letraStyle = { font: { italic: true, sz: 10 }, alignment: { vertical: "center", wrapText: true } };
const totalDestacadoTextoStyle = {
  ...totalTextoStyle,
  fill: { fgColor: { rgb: "E8E8E8" } },
};
const totalDestacadoMoneyStyle = {
  ...totalMoneyStyle,
  fill: { fgColor: { rgb: "E8E8E8" } },
};
const totalEtiquetaStyle = { font: { bold: true, sz: 10 }, alignment: { horizontal: "right", vertical: "center" } };
// La descripción de compras trae las observaciones de la partida en un segundo
// renglón, como en el PDF.
const descripcionStyle = { ...textStyle, alignment: { vertical: "center", wrapText: true } };

const vacia = () => ({ v: "" });

const importeDe = (nodo) => aNumero(nodo?.Importe !== undefined ? nodo.Importe : nodo?.["@Importe"]);

const sumaTraslados = (concepto) =>
  (concepto?.Impuestos?.Traslados || []).reduce((total, impuesto) => total + importeDe(impuesto), 0);

// Ventas — A: Clave · B: Clave SAT · C: Descripción · D: Unidad · E: Cantidad
// F: P. unitario · G: Descuento · H: Impuestos · I: Importe
const COLUMNAS_VENTA = [
  { titulo: "Clave", ancho: 18, celda: (concepto) => celdaTexto(concepto.NoIdentificacion) },
  { titulo: "Clave SAT", ancho: 14, celda: (concepto) => celdaTexto(concepto.ClaveProdServ) },
  { titulo: "Descripcion", ancho: 42, celda: (concepto) => celdaTexto(concepto.Descripcion) },
  { titulo: "Unidad", ancho: 12, celda: (concepto) => celdaTexto(concepto.Unidad) },
  { titulo: "Cantidad", ancho: 10, celda: (concepto) => celdaNumero(concepto.Cantidad) },
  { titulo: "Precio unitario", ancho: 16, celda: (concepto) => celdaMoneda(concepto.ValorUnitario) },
  { titulo: "Descuento", ancho: 14, celda: (concepto) => celdaMoneda(aNumero(concepto.Descuento)) },
  { titulo: "Impuestos", ancho: 14, celda: (concepto) => celdaMoneda(sumaTraslados(concepto)) },
  { titulo: "Importe", ancho: 16, esImporte: true, celda: (concepto) => celdaMoneda(aNumero(concepto.Importe)) },
];

// Compras — las claves que importan son la interna y la del proveedor, y cada
// partida puede llevar su propia fecha de entrega.
const COLUMNAS_COMPRA = [
  { titulo: "Clave Int.", ancho: 18, celda: (concepto) => celdaTexto(concepto.ClaveInt) },
  { titulo: "Clave Prov.", ancho: 14, celda: (concepto) => celdaTexto(concepto.ClaveProv) },
  {
    titulo: "Descripcion",
    ancho: 42,
    celda: (concepto) => celdaTexto(
      (concepto.Descripcion || "") + (concepto.Observaciones ? "\n" + concepto.Observaciones : ""),
      descripcionStyle
    ),
  },
  { titulo: "Unidad", ancho: 12, celda: (concepto) => celdaTexto(concepto.descUnidad || concepto.Unidad) },
  { titulo: "Cantidad", ancho: 10, celda: (concepto) => celdaNumero(concepto.Cantidad) },
  { titulo: "Precio unitario", ancho: 16, celda: (concepto) => celdaMoneda(concepto.ValorUnitario) },
  { titulo: "Descuento", ancho: 14, celda: (concepto) => celdaMoneda(aNumero(concepto.Descuento)) },
  { titulo: "Impuestos", ancho: 14, celda: (concepto) => celdaMoneda(sumaTraslados(concepto)) },
  { titulo: "Importe", ancho: 16, esImporte: true, celda: (concepto) => celdaMoneda(aNumero(concepto.Importe)) },
  { titulo: "Fecha entrega", ancho: 14, celda: (concepto) => celdaFecha(concepto.FechaEntrega) },
];

/**
 * Mismo nombre que imprime el PDF para cada traslado: IVA / IEPS con su tasa, o
 * el nombre del impuesto local tal cual viene en el complemento.
 */
const nombreTraslado = (impuesto) => {
  let nombre;
  if (impuesto.Impuesto === "002") nombre = "IVA";
  else if (impuesto.Impuesto === "003") nombre = "IEPS";
  else return impuesto["@ImpLocTrasladado"] || "Impuesto";

  if (impuesto.TipoFactor === "Tasa") {
    nombre += " " + round2Decimals(impuesto.TasaOCuota * 100) + "%";
  } else if (impuesto.TipoFactor === "Cuota") {
    nombre += " $" + round2Decimals(impuesto.TasaOCuota);
  }
  return nombre;
};

const nombreRetencion = (retencion) => {
  if (retencion.Impuesto === "001") return "Retención ISR";
  if (retencion.Impuesto === "002") return "Retención IVA";
  return retencion["@ImpLocRetenido"] || "Retención";
};

const direccionEmpresa = (empresa) => [
  [empresa?.calle, empresa?.numeroCalle, empresa?.numeroInterior].filter(Boolean).join(" "),
  [
    [empresa?.colonia?.tipoAsentamiento, empresa?.colonia?.nombre].filter(Boolean).join(" "),
    empresa?.colonia?.cp ? "C.P. " + empresa.colonia.cp : "",
    empresa?.municipio?.nombre,
    empresa?.estado?.abreviatura,
    empresa?.pais?.abreviatura,
  ].filter(Boolean).join(" "),
].filter(Boolean).join(", ");

const contactoEmpresa = (empresa) => {
  const partes = [];
  if (empresa?.telefono) partes.push("Tel: " + PhoneMask(empresa.telefono));
  if (empresa?.correo) partes.push(empresa.correo);
  return partes.join("  |  ");
};

const nombreEmpresa = (empresa) => {
  if (empresa?.nombreComercial) return empresa.nombreComercial;
  if (empresa?.persona) return empresa.nombre || "";
  return [empresa?.nombre, empresa?.apellidoPaterno, empresa?.apellidoMaterno].filter(Boolean).join(" ");
};

const direccionEntregaTexto = (direccion) => {
  if (!direccion) return "";
  if (typeof direccion === "string") return direccion;
  return [
    [direccion.calle, direccion.numeroCalle, direccion.numeroInterior].filter(Boolean).join(" "),
    direccion?.colonia?.nombre,
    direccion?.colonia?.cp ? "C.P. " + direccion.colonia.cp : "",
    direccion?.municipio?.nombre,
    direccion?.estado?.abreviatura,
  ].filter(Boolean).join(", ");
};

/**
 * Renglón del encabezado: etiqueta+valor a la izquierda (A/C) y otro par a la
 * derecha (F/H). Cualquiera de los dos puede ir vacío.
 */
const filaEncabezado = ({ etiqueta, valor, etiquetaStyle: eIzq, valorStyle: vIzq, etiquetaDer, valorDer, valorDerCelda }, filaVacia) => {
  const fila = filaVacia();
  if (etiqueta !== undefined) fila[0] = celdaTexto(etiqueta, eIzq || etiquetaStyle);
  if (valor !== undefined) fila[2] = celdaTexto(valor, vIzq || valorStyle);
  if (etiquetaDer !== undefined) fila[5] = celdaTexto(etiquetaDer, etiquetaStyle);
  if (valorDerCelda !== undefined) fila[7] = valorDerCelda;
  else if (valorDer !== undefined) fila[7] = celdaTexto(valorDer, valorStyle);
  return fila;
};

/**
 * @param {Object} values  la respuesta de `<modulo>/datosPdf` (o un elemento de
 *                         `datosPdfMasivo`), tal cual la consume el PDF
 * @param {Object} modulo  configuración del módulo: `titulo(values)`, `esVenta`,
 *                         `esCompra`
 * @returns {Promise<Blob>} el .xlsx listo para descargar o meter al ZIP
 */
const construirExcelDocumento = async (values, modulo) => {
  const respuestaFactura = values?.respuestaFactura || {};
  const empresa = respuestaFactura.empresa || {};
  const dataSat = respuestaFactura.dataSat || {};
  const receptor = dataSat.Receptor || {};

  // En compras el receptor del documento es el proveedor, y la partida trae dos
  // claves propias más su fecha de entrega.
  const esCompra = !!modulo?.esCompra;
  const columnas = esCompra ? COLUMNAS_COMPRA : COLUMNAS_VENTA;
  const COL_ULTIMA = columnas.length - 1;
  const filaVacia = () => columnas.map(() => vacia());

  const titulo = typeof modulo?.titulo === "function" ? modulo.titulo(values) : (modulo?.titulo || "Documento");
  const numero = [dataSat.Serie, dataSat.Folio].filter((v) => v !== undefined && v !== null && v !== "").join(" - ");

  const filas = [];
  const merges = [];
  // Une A:B (etiqueta), C:E (valor izquierdo), F:G (etiqueta derecha) y H:I
  // (valor derecho) del renglón que se acaba de empujar.
  const unirEncabezado = (indice) => {
    merges.push(
      { s: { r: indice, c: 0 }, e: { r: indice, c: 1 } },
      { s: { r: indice, c: 2 }, e: { r: indice, c: 4 } },
      { s: { r: indice, c: 5 }, e: { r: indice, c: 6 } },
      { s: { r: indice, c: 7 }, e: { r: indice, c: COL_ULTIMA } },
    );
  };
  const empujarEncabezado = (config) => {
    filas.push(filaEncabezado(config, filaVacia));
    unirEncabezado(filas.length - 1);
  };

  // ── Empresa (izquierda) y datos del documento (derecha) ───────────────────
  empujarEncabezado({
    valor: nombreEmpresa(empresa), valorStyle: tituloEmpresaStyle,
    etiquetaDer: titulo + ":", valorDer: numero,
  });
  // El número del documento en grande, como en el PDF.
  filas[filas.length - 1][7] = celdaTexto(numero, tituloDocumentoStyle);

  empujarEncabezado({
    valor: "R.F.C: " + (empresa.rfc || ""),
    etiquetaDer: "Fecha:", valorDerCelda: celdaFecha(dataSat.Fecha, valorFechaStyle),
  });
  empujarEncabezado({
    valor: direccionEmpresa(empresa),
    etiquetaDer: "Moneda:",
    valorDer: (dataSat.Moneda || "") + (aNumero(dataSat.TipoCambio) && aNumero(dataSat.TipoCambio) !== 1
      ? "   T.C. " + round2Decimals(dataSat.TipoCambio)
      : ""),
  });
  empujarEncabezado({
    valor: contactoEmpresa(empresa),
    etiquetaDer: "Condiciones de pago:", valorDer: dataSat.CondicionesDePago,
  });
  empujarEncabezado({
    valor: empresa?.regimenFiscal
      ? `Régimen: (${empresa.regimenFiscal.c_RegimenFiscal}) ${empresa.regimenFiscal.descripcion || ""}`.trim()
      : "",
    etiquetaDer: "Método de pago:", valorDer: dataSat.MetodoPago,
  });

  filas.push(filaVacia());

  // ── Cliente / proveedor (izquierda) y referencias (derecha) ───────────────
  empujarEncabezado({
    etiqueta: esCompra ? "Proveedor:" : "Cliente:", valor: receptor.Nombre,
    // La OC no referencia otra orden de compra; su dato útil es la cotización
    // que le dio origen.
    etiquetaDer: esCompra ? "Cotizacion:" : "Orden de compra:",
    valorDer: esCompra ? dataSat.cotizacionOC : dataSat.pedido,
  });
  empujarEncabezado({
    etiqueta: "R.F.C:", valor: receptor.Rfc,
    etiquetaDer: esCompra ? "Solicitó:" : "Vendedor:",
    valorDer: esCompra ? dataSat.nombreCreador : nombreVendedor(values.vendedor),
  });
  empujarEncabezado({
    etiqueta: "Atención:", valor: dataSat.atencion,
    etiquetaDer: "Proyecto:", valorDer: esCompra ? dataSat.ProyectoNombre : dataSat.proyecto,
  });
  if (esCompra) {
    empujarEncabezado({
      etiqueta: "Destino:", valor: dataSat.destino,
      etiquetaDer: "Autorizó:", valorDer: dataSat.nombreAprobador,
    });
    empujarEncabezado({
      etiqueta: "Embarque:", valor: dataSat.embarque,
      etiquetaDer: "Fecha de entrega:",
      valorDerCelda: celdaFecha(dataSat.FechaEntrega, valorFechaStyle),
    });
  }
  const entrega = esCompra
    ? direccionEntregaTexto(dataSat.DireccionEntrega)
    : direccionEntregaTexto(dataSat.direccionEntrega);
  if (entrega) {
    empujarEncabezado({ etiqueta: "Entrega:", valor: entrega });
  }

  filas.push(filaVacia());

  // ── Tabla de partidas ─────────────────────────────────────────────────────
  filas.push(columnas.map((columna) => celdaTexto(columna.titulo, headerStyle)));

  for (const concepto of dataSat.Conceptos || []) {
    filas.push(columnas.map((columna) => columna.celda(concepto)));
  }

  filas.push(filaVacia());

  // ── Totales ───────────────────────────────────────────────────────────────
  // Los impuestos locales viven en el complemento, fuera del nodo Impuestos:
  // si no se concatenan aquí, el TOTAL no coincide con el del PDF.
  const locales = nodoImpuestosLocales(dataSat);
  const traslados = [
    ...(Array.isArray(dataSat?.Impuestos?.Traslados) ? dataSat.Impuestos.Traslados : []),
    ...(locales?.["implocal:TrasladosLocales"] || []),
  ];
  const retenciones = [
    ...(Array.isArray(dataSat?.Impuestos?.Retenciones) ? dataSat.Impuestos.Retenciones : []),
    ...(locales?.["implocal:RetencionesLocales"] || []),
  ];

  const totalTraslados = aNumero(dataSat?.Impuestos?.TotalImpuestosTrasladados)
    + aNumero(locales?.["@TotaldeTraslados"]);
  const totalRetenciones = aNumero(dataSat?.Impuestos?.TotalImpuestosRetenidos)
    + aNumero(locales?.["@TotaldeRetenciones"]);

  const subtotal = aNumero(dataSat.SubTotal);
  const descuentoTotal = aNumero(dataSat.Descuento);
  // Misma fórmula que el PDF: el TOTAL no descuenta retenciones; ésas salen
  // después, en TOTAL PAGO.
  const total = subtotal - descuentoTotal + totalTraslados;

  // El total cae en la columna de Importe (que en compras no es la última: ahí
  // cierra la fecha de entrega), y la etiqueta ocupa las dos de su izquierda.
  const colImporte = columnas.findIndex((columna) => columna.esImporte);
  const colEtiqueta = colImporte - 2;

  const filaTotal = (etiqueta, valor, destacado = false) => {
    const fila = filaVacia();
    fila[colEtiqueta] = celdaTexto(etiqueta, destacado ? totalDestacadoTextoStyle : totalEtiquetaStyle);
    fila[colEtiqueta + 1] = { v: "", s: destacado ? totalDestacadoTextoStyle : totalEtiquetaStyle };
    fila[colImporte] = celdaMoneda(valor, destacado ? totalDestacadoMoneyStyle : moneyStyle);
    filas.push(fila);
    merges.push({ s: { r: filas.length - 1, c: colEtiqueta }, e: { r: filas.length - 1, c: colEtiqueta + 1 } });
  };

  filaTotal("Subtotal:", subtotal);
  if (descuentoTotal > 0) filaTotal("Descuento:", -descuentoTotal);
  for (const traslado of traslados) filaTotal(nombreTraslado(traslado) + ":", importeDe(traslado));
  filaTotal("TOTAL:", total, true);
  for (const retencion of retenciones) filaTotal(nombreRetencion(retencion) + ":", -importeDe(retencion));
  // Igual que el PDF: el renglón de pago sólo aparece cuando hay retenciones.
  if (totalRetenciones > 0) filaTotal("TOTAL PAGO:", aNumero(dataSat.Total), true);

  // ── Cantidad con letra y observaciones ────────────────────────────────────
  const totalConLetra = totalRetenciones > 0 ? aNumero(dataSat.Total) : total;
  filas.push(filaVacia());
  filas.push([celdaTexto("Son: " + numeroALetra(totalConLetra, dataSat.Moneda), letraStyle), ...filaVacia().slice(1)]);
  merges.push({ s: { r: filas.length - 1, c: 0 }, e: { r: filas.length - 1, c: COL_ULTIMA } });

  if (values.imprimirObservaciones && values.observaciones) {
    filas.push(filaVacia());
    filas.push([celdaTexto("Observaciones:", etiquetaStyle), ...filaVacia().slice(1)]);
    merges.push({ s: { r: filas.length - 1, c: 0 }, e: { r: filas.length - 1, c: COL_ULTIMA } });
    filas.push([celdaTexto(values.observaciones, valorStyle), ...filaVacia().slice(1)]);
    merges.push({ s: { r: filas.length - 1, c: 0 }, e: { r: filas.length - 1, c: COL_ULTIMA } });
  }

  // ── Escritura ─────────────────────────────────────────────────────────────
  const hoja = XLSXStyle.utils.aoa_to_sheet(filas);
  hoja["!cols"] = columnas.map((columna) => ({ wch: columna.ancho }));
  hoja["!merges"] = merges;

  const libro = XLSXStyle.utils.book_new();
  // El nombre de hoja no admite / \ ? * [ ] : ni más de 31 caracteres.
  XLSXStyle.utils.book_append_sheet(libro, hoja, titulo.replace(/[/\\?*[\]:]/g, "").slice(0, 31));

  const buffer = XLSXStyle.write(libro, { bookType: "xlsx", type: "array" });

  // El logo se inyecta a mano en el zip del .xlsx; si no se puede (sin logo, o
  // en un formato que Excel no acepta como Default), se baja igual sin imagen.
  const conLogo = await inyectarLogoEnExcel(buffer, empresa.logo, { fila: 0, columna: 0 });
  if (conLogo) return conLogo;

  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

export default construirExcelDocumento;
